import { formatLink, getFullImageUrl } from "@/lib/utils";
import { SearchTrendItem } from "@/services/search-trends/types";

export interface HeaderQueryResponse {
  Header: {
    topBar: {
      platformLabel: string;
      enableLanguageSelector: boolean;
      enableSearch: boolean;
      searchPlaceholder: string;
    };
    mainNav: {
      logo: {
        alt: string;
        url: string;
      };
      ctaButton: {
        label: string;
        target: string | null;
        linkType: string | boolean;
        internalLink: {
          value: {
            slug: string;
          };
        } | null;
        externalURL: string | null;
      }
      navItems: Array<{
        label: string;
        navType: string | boolean;
        internalLink: {
          value: {
            slug: string;
          };
        } | null;
        externalURL: string | null;
        target: string | null;
        megaMenutitle: string | null;
        megaMenulinks: Array<{
          label: string;
          linkType: string | boolean;
          internalLink: {
            value: {
              slug: string;
            };
          } | null;
          externalURL: string | null;
          target: string | null;
        }> | null;
      }>;
    };
  };
}
export interface MenuItem {
  id: string;
  title: string;
  hasSubmenu: boolean;
  href?: string;
  target?: string;
  submenu?: {
    title: string;
    subtitle: string;
    links: Array<{
      href: string;
      text: string;
      target?: string;
    }>;
  };
}

export interface TopBarProps {
  platformLabel: string;
  enableLanguageSelector: boolean;
  enableSearch: boolean;
  searchPlaceholder: string;
}

export interface LogoProps {
  alt: string;
  url: string;
}

export interface CtaButtonProps {
  label: string;
  url: string;
  target: string | null;
}

export interface HeaderProps {
  topBar?: TopBarProps;
  logo?: LogoProps;
  ctaButton?: CtaButtonProps;
  menuItems: MenuItem[];
  searchTrends?: SearchTrendItem[];
}

export const mapHeader = (response: HeaderQueryResponse): HeaderProps => {
  if (!response || !response?.Header || !response?.Header?.mainNav) {
    return {
      menuItems: []
    };
  }
  const menuItems: MenuItem[] = response?.Header?.mainNav.navItems?.map((item) => {
    const href = formatLink(item);
    const id = item?.label?.toLowerCase()?.replace(/\s+/g, '-');
    
    const hasSubmenu = !!item.megaMenulinks && item?.megaMenulinks.length > 0;
    
    const submenu = hasSubmenu ? {
      title: item?.label,
      subtitle: item?.megaMenutitle || '',
      links: item?.megaMenulinks!.map(link => {
        const linkHref = formatLink(link);
        
        return {
          href: linkHref,
          text: link?.label,
          target: link?.target || undefined
        };
      })
    } : undefined;

    return {
      id,
      title: item.label,
      hasSubmenu,
      href: hasSubmenu ? undefined : href,
      target: item.target || undefined,
      submenu
    };
  });

  return {
    topBar: {
      platformLabel: response?.Header?.topBar?.platformLabel || '',
      enableLanguageSelector: response?.Header?.topBar?.enableLanguageSelector || false,
      enableSearch: response?.Header?.topBar?.enableSearch || false,
      searchPlaceholder: response?.Header?.topBar?.searchPlaceholder || '',
    },
    logo: {
      alt: response?.Header?.mainNav?.logo?.alt || '',
      url: response?.Header?.mainNav?.logo?.url ? getFullImageUrl(response?.Header?.mainNav?.logo?.url) : '',
    },
    ctaButton: {
      label: response?.Header?.mainNav?.ctaButton?.label || '',
      url: formatLink(response?.Header?.mainNav?.ctaButton),
      target: response?.Header?.mainNav?.ctaButton?.target || "_blank",
    },
    menuItems
  };
};

export function getMenuById(menuItems: MenuItem[], id: string): MenuItem | undefined {
  if (!Array.isArray(menuItems)) {
    return undefined;
  }
  return menuItems?.find(menu => menu?.id === id);
}